import Joi, { ValidationResult } from 'joi';
import _ from 'lodash';

import {
    activeFromDateErrorSchema,
    activeToDateErrorSchema,
    childPacksWarningSchema,
    computeIsBundleWorthCovered,
    contentWarningSchema,
    displayName,
    features,
    packTypeSchema,
    scenesWarningSchema,
    sharedErrorNumericFields,
    sharedWarningFields,
} from './packValidation.shared';

import { PackType } from '../types/enums/packtype.enum';

import { PackListItem, PackListPriceLevel } from '../types/packTypes';

type PackListValidationItem = PackListItem & {
    activeToDateCopy: PackListItem['activeToDate'];
    childPacks: PackListItem[];
    isBundleWorthCovered?: boolean;
    hasValidPriceLevel?: boolean;
    hasFailedMedias?: boolean;
};

const createSchemas = (item: PackListValidationItem) => {
    const { HOME_DESIGNER } = PackType;

    // Check if bundle worth is covered (for multipacks)
    item.isBundleWorthCovered = computeIsBundleWorthCovered(
        item.childPacks,
        item.packPriceLevels,
    );

    // Check if at least one packPriceLevel has a non-empty SKU
    item.hasValidPriceLevel = item.packPriceLevels.some(
        (pl: PackListPriceLevel) =>
            pl.googleSKU !== '' ||
            pl.appleSKU !== '' ||
            pl.amazonSKU !== '' ||
            (pl.xsollaSKU !== null && pl.xsollaSKU !== ''),
    );

    item.hasFailedMedias = item.medias.some(
        (media) => media?.processingStatus === 'error',
    );

    const packWarningSchema = Joi.object()
        .options({ abortEarly: false })
        .keys({
            ...sharedWarningFields,
            displayName,
            features,
            type: packTypeSchema(item.type),
            scenes: scenesWarningSchema,
            content: contentWarningSchema,
            childPacks: childPacksWarningSchema(
                item.requiredVersion,
                item.childPacks,
            ),
            locations: Joi.when('type', {
                is: HOME_DESIGNER,
                then: Joi.number().integer().min(1).required(),
                otherwise: Joi.number().integer().min(0).required(),
            }).messages({
                'number.min': `${HOME_DESIGNER} should have at least 1 Location`,
            }),
            characters: Joi.number().integer().required(),
            templates: Joi.number().integer(),
            furniture: Joi.number().integer(),
            surfaceDesigns: Joi.number().integer(),
            garments: Joi.number().integer(),
            patterns: Joi.number().integer(),
            stickers: Joi.number().integer(),
            medias: Joi.array().items(Joi.object()),
            hasFailedMedias: Joi.boolean().valid(false).messages({
                'any.only':
                    'One or more media files failed to process — remove and re-upload them',
            }),
            packPriceLevels: Joi.array().items(Joi.object()).required(),
            childPackIds: Joi.array().items(Joi.string()).required(),
            id: Joi.number(),
            isBundleWorthCovered: Joi.boolean().valid(true).messages({
                'any.only':
                    'Bundle worth is not covered by any pack price level',
            }),
            hasValidPriceLevel: Joi.boolean().valid(true).messages({
                'any.only':
                    'Pack must have at least one pack price level with a non-empty SKU',
            }),
        });

    const packErrorSchema = Joi.object()
        .options({ abortEarly: false })
        .keys({
            packId: Joi.string(),
            name: Joi.string(),
            requiredVersion: Joi.number().integer(),
            worth: Joi.number().integer(),
            priority: Joi.number().integer(),
            displayName: Joi.object(),
            features: Joi.object(),
            type: packTypeSchema(item.type),
            activeFromDate: activeFromDateErrorSchema,
            activeToDate: activeToDateErrorSchema,
            activeToDateCopy: Joi.any(),
            scenes: Joi.array().required(),
            content: Joi.array().required(),
            childPacks: Joi.array().items(Joi.object()).required(),
            ...sharedErrorNumericFields,
            outfits: Joi.number().integer(),
            accessories: Joi.number().integer(),
            hairStyles: Joi.number().integer(),
            medias: Joi.array().items(Joi.object()),
            hasFailedMedias: Joi.boolean(),
            tags: Joi.array().items(Joi.number().integer()),
            packPriceLevels: Joi.array()
                .min(1)
                .items(Joi.object())
                .required()
                .messages({
                    'array.min': `Pack must contain at least 1 pack price level`,
                }),
            childPackIds: Joi.array().items(Joi.string()).required(),
            id: Joi.number(),
            isBundleWorthCovered: Joi.boolean(),
            hasValidPriceLevel: Joi.boolean(),
            carouselIds: Joi.array(),
        });

    return {
        packWarningSchema,
        packErrorSchema,
    };
};

function validateListPack(
    item: PackListItem,
    childPacksData: PackListItem[],
): {
    errors: ValidationResult;
    warnings: ValidationResult;
} {
    const validationItem: PackListValidationItem = Object.assign(
        _.cloneDeep(item),
        {
            activeToDateCopy: item.activeToDate,
            childPacks: childPacksData,
        },
    );

    const { packWarningSchema, packErrorSchema } =
        createSchemas(validationItem);
    const warnings = packWarningSchema.validate(validationItem);
    const errors = packErrorSchema.validate(validationItem);
    return { errors, warnings };
}

export default validateListPack;
